import { SchemaInterface } from '../../shared/types/schema-interface';
import { Schema } from 'app/core/_models/schema';
import * as intlTelInputGlobals from 'intl-tel-input/build/js/intlTelInputWithUtils.js';
import { IdentifierType } from './member-interface';

export interface MobileAppLink {
  url: string;
  legacy_app_url: string;
  slug: string;
  name: string;
}

export interface MobileAppSetting {
  universal_links_enabled: boolean;
  android_app_id: string;
  android_certificate_hash: string;
  ios_app_id: string;
  ios_bundle_id: string;
  ios_team_id: string;
  links: MobileAppLink[];
}

export interface MemberAuthenticationConfigurationSocialLogin {
  provider: string;
  config: object;
}

export interface MemberAuthenticationConfiguration {
  enabled: boolean;
  forced_identifier?: IdentifierType;
  social_logins?: MemberAuthenticationConfigurationSocialLogin[];
}

export class Settings {
  public name: string;
  public subunit_name: string;
  public slug: string;
  public logo_url: string;
  public contact_phone: string;
  public contact_email: string;
  public website_url: string;
  public facebook_url: string;
  public instagram_url: string;
  public tiktok_url: string;
  public linkedin_url: string;
  public schema: SchemaInterface;
  public schemaObject: Schema;
  public custom_js: string;
  public ga_tracking_id: string;
  public fb_tracking_id: string;
  public gtm_tracking_id: string;
  public ga_linker_enabled: boolean;
  public ga_linker_domains: string[];
  public advanced_terms_of_service_enabled: boolean;
  public advanced_terms_of_service: string;
  public additional_index_html: string;
  public offers_enabled: boolean;
  public games_enabled: boolean;
  public spaycial_integration_enabled: boolean;
  public coupon_preview_enabled: boolean;
  public default_country: string;
  public mobile_app_setting: MobileAppSetting;
  public login_enabled: boolean;
  public unsubscription_redirection_url: string;
  public open_store_when_mobile: boolean;
  public member_card_qr_code_displayed: boolean;
  public forced_profile_update: boolean;
  public forced_identifier: string;
  public redirection_url: string;
  public minimum_member_age: number;
  public new_cookie_banner: boolean;
  public transactions_currency: string;
  public member_authentication_configuration: MemberAuthenticationConfiguration;
  private available_msisdn_country_codes?: string[];
  public new_auth_enabled: boolean;

  constructor(settings: any) {
    Object.keys(settings).forEach((key) => this[key] = settings[key]);
    this.schemaObject = new Schema(this.schema);
  }

  public iosStoreLink(): string | null {
    if (!this.mobile_app_setting) { return null; }
    return `https://apps.apple.com/app/id${this.mobile_app_setting.ios_app_id}`;
  }

  public getSubunitDisplayName(): string {
    return this.subunit_name || this.name;
  }

  public androidStoreLink(source?: string): string | null {
    if (!this.mobile_app_setting) { return null; }
    return `https://play.google.com/store/apps/details?id=${this.mobile_app_setting.android_app_id}`
      + `&utm_source=boostcom-member-webforms`
      + `${source ? `&utm_campaign=${source}` : ''}`
      + `&pcampaignid=MKT-Other-global-all-co-prtnr-py-PartBadge-Mar2515-1`;
  }

  public suggestedMsisdnCountry(): string | null {
    return this.default_country ? this.default_country.toLowerCase() : null;
  }

  public suggestedMsisdnCountryDialCode(): string | null {
    return intlTelInputGlobals.getCountryData().find(country => this.suggestedMsisdnCountry() === country.iso2).dialCode;
  }

  public getAvailableMsisdnCountryCodes(): string[] {
    return this.available_msisdn_country_codes || [];
  }

  public availableMsisdnCountries(): string[] {
    return intlTelInputGlobals.getCountryData()
      .filter(country => this.getAvailableMsisdnCountryCodes().includes(country.dialCode))
      .map(country => country.iso2);
  }

  public isHybridLoginFlow(): boolean {
    return this.schemaObject.hasMultipleIdentifiers();
  }

  public hasOnlyAppWithoutWebforms(): boolean {
    return !this.login_enabled && !!this.mobile_app_setting;
  }

  public shouldRedirectToExternalUrl(): boolean {
    return !this.login_enabled && !!this.redirection_url;
  }

}
